import React, { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Input, Select } from "antd";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useSelector } from "react-redux";
import { storage } from "../../../config/firebase";
import { newClientThemeVars } from "../../../theme/globalStyle/globalStyle";
import theme from "../../../theme";
import vehicleService from "../../../service/vehicle.service";
import {
  validatorCPF,
  validatorCNPJ,
} from "../../../components/validators/validators";
import {
  createPendingStorageId,
  embeddedReboqueToForm,
  emptyReboque,
  mapEmbeddedReboquesToApiPayload,
  normalizeTrailerIds,
  patchEmbeddedReboque,
  reboqueFormToTrailerCreatePayload,
  reboqueFormToTrailerUpdatePayload,
  trailerDocToReboqueForm,
  unwrapVehicleResponse,
} from "../../../util/vehicle/reboque-trailer-utils";
import { saveEmbeddedReboqueOnTractor } from "../../../util/vehicle/reboque-trailer-persistence";
import {
  focusFirstInvalidField,
  isReboqueStarted,
  isVehicleTypeRequiringReboque,
  validateReboqueForm,
  validateVehicleForm,
} from "../validation";
import DocumentField from "./DocumentField";
import ReboqueFormFields from "./ReboqueFormFields";
import VehicleTrailersSection from "./VehicleTrailersSection";
import {
  BODYWORK_TYPES,
  RNTRC_TYPE_OPTIONS,
  STATES_UF,
  VEHICLE_TYPES,
} from "./vehicle-form-constants";
import "../vehicle.css";

/** Máscara pt-BR para peso/capacidade em kg (ex.: 1000 → 1.000). */
function formatWeightMask(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return parseInt(digits, 10).toLocaleString("pt-BR");
}

const ALERT_TITLE_ID = "quick-vehicle-alert-dialog-title";
const ALERT_DESCRIPTION_ID = "quick-vehicle-alert-dialog-description";

const createInitialFormState = () => ({
  vehiclePlate: "",
  renavam: "",
  ufVeiculo: "",
  documentCRLV: "",
  descricaoVeiculo: "",
  vehicleType: "",
  rntrc: "",
  tipoRNTRC: "",
  bodyworkType: "",
  bodyworkPlate: "",
  documentAnttFrontImg: "",
  pesoTara: "",
  capacidadeKG: "",
  capacidadeM3: "",
  tipoProprietario: "cpf",
  cpfoucnpj: "",
  nomeProprietario: "",
  vehicleImg: "",
  trailerIds: [],
  reboques: [],
});

function parseDialogMessageItems(msg) {
  if (Array.isArray(msg)) {
    return msg.map((item) => String(item || "").trim()).filter(Boolean);
  }

  if (msg == null || msg === "") return [];

  return String(msg)
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter((line) => {
      if (!line) return false;
      const normalized = line.toLowerCase();
      return (
        !normalized.includes("seguinte") &&
        !normalized.includes("obrigatório(s) estão vazio")
      );
    });
}

function isRequiredFieldsDialog(dialogTitle) {
  return String(dialogTitle || "")
    .toLowerCase()
    .includes("obrigat");
}

function getSelectPopupContainer(trigger) {
  return trigger?.closest?.(".MuiDialog-root") || document.body;
}

function resolveSavedVehicle(response, payload, fallbackId) {
  const saved = unwrapVehicleResponse(response);
  if (saved?.id) {
    return { ...payload, ...saved };
  }
  if (fallbackId) {
    return { ...payload, id: fallbackId };
  }
  return saved;
}

/** ID persistido na API (ignora placeholders embutidos do motorista). */
export function normalizePersistedVehicleId(value) {
  const id = String(value || "").trim();
  if (!id || id === "undefined" || id.startsWith("embedded-")) {
    return "";
  }
  return id;
}

function mapVehicleToForm(data) {
  if (!data || typeof data !== "object") {
    return createInitialFormState();
  }

  return {
    vehiclePlate: data.vehiclePlate || "",
    renavam: data.renavam || "",
    ufVeiculo: data.ufVeiculo || "",
    documentCRLV: data.documentCRLV || "",
    descricaoVeiculo: data.descricaoVeiculo || "",
    vehicleType: data.vehicleType || "",
    rntrc: data.rntrc || "",
    tipoRNTRC: data.tipoRNTRC || "",
    bodyworkType: data.bodyworkType || "",
    bodyworkPlate: data.bodyworkPlate || "",
    documentAnttFrontImg: data.documentAnttFrontImg || "",
    pesoTara: formatWeightMask(data.pesoTara || ""),
    capacidadeKG: formatWeightMask(data.capacidadeKG || ""),
    capacidadeM3: formatWeightMask(data.capacidadeM3 || ""),
    tipoProprietario: data.tipoProprietario || "cpf",
    cpfoucnpj: data.cpfoucnpj || "",
    nomeProprietario: data.nomeProprietario || "",
    vehicleImg: data.vehicleImg || "",
    trailerIds: normalizeTrailerIds(data.trailerIds),
    reboques: Array.isArray(data.reboques)
      ? data.reboques.map((reboque) => embeddedReboqueToForm(reboque))
      : [],
  };
}

function QuickVehicleModal({ open, onClose, vehicleId = null, onSaved, onCreated }) {
  const user = useSelector((state) => state.user);
  const savingRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;
  const loadSeqRef = useRef(0);
  const editingIdRef = useRef("");
  const pendingStorageIdRef = useRef("");

  const persistedVehicleId = normalizePersistedVehicleId(vehicleId);
  const isEditMode = Boolean(persistedVehicleId);

  const [form, setForm] = useState(createInitialFormState);
  const [isReboque, setIsReboque] = useState(false);
  const [reboqueForm, setReboqueForm] = useState(emptyReboque);
  const [carregando, setCarregando] = useState(false);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [lastWarnedCpfCnpj, setLastWarnedCpfCnpj] = useState("");

  const {
    vehiclePlate,
    renavam,
    ufVeiculo,
    documentCRLV,
    descricaoVeiculo,
    vehicleType,
    rntrc,
    tipoRNTRC,
    bodyworkType,
    bodyworkPlate,
    documentAnttFrontImg,
    pesoTara,
    capacidadeKG,
    capacidadeM3,
    tipoProprietario,
    cpfoucnpj,
    nomeProprietario,
    vehicleImg,
    trailerIds,
    reboques,
  } = form;

  const requiresReboque = isVehicleTypeRequiringReboque(vehicleType);
  const capacityFieldsDisabled = requiresReboque;
  const isBusy = carregando || uploading || loadingVehicle;

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const resetTransientState = () => {
    setForm(createInitialFormState());
    setIsReboque(false);
    setReboqueForm(emptyReboque());
    setFieldErrors({});
    setHasAttemptedSubmit(false);
    setLastWarnedCpfCnpj("");
    setAlertOpen(false);
    setAlertMsg("");
    setAlertTitle("");
    setCarregando(false);
    setLoadingVehicle(false);
    setUploading(false);
    savingRef.current = false;
    editingIdRef.current = "";
    pendingStorageIdRef.current = "";
  };

  useEffect(() => {
    if (open) return;
    loadSeqRef.current += 1;
    resetTransientState();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const seq = ++loadSeqRef.current;
    setForm(createInitialFormState());
    setIsReboque(false);
    setReboqueForm(emptyReboque());
    setFieldErrors({});
    setHasAttemptedSubmit(false);
    setLastWarnedCpfCnpj("");
    setAlertOpen(false);
    setAlertMsg("");
    setAlertTitle("");
    savingRef.current = false;
    editingIdRef.current = "";
    pendingStorageIdRef.current = createPendingStorageId();

    const idToEdit = normalizePersistedVehicleId(vehicleId);
    if (!idToEdit) {
      setLoadingVehicle(false);
      return undefined;
    }

    let cancelled = false;
    editingIdRef.current = idToEdit;
    pendingStorageIdRef.current = idToEdit;
    setLoadingVehicle(true);

    (async () => {
      try {
        const response = await vehicleService.getById(idToEdit);
        if (cancelled || seq !== loadSeqRef.current || !openRef.current) {
          return;
        }

        const data = unwrapVehicleResponse(response);
        if (!data) {
          setAlertTitle("Erro");
          setAlertMsg("Não foi possível carregar o veículo.");
          setAlertOpen(true);
          return;
        }

        if (data.id && normalizePersistedVehicleId(data.id) !== idToEdit) {
          return;
        }

        const asReboque = data.trailers === true;
        setIsReboque(asReboque);
        if (asReboque) {
          setReboqueForm(trailerDocToReboqueForm({ ...data, id: idToEdit }));
        } else {
          const tractor = { ...data, id: idToEdit };
          if (cancelled || seq !== loadSeqRef.current || !openRef.current) {
            return;
          }
          setForm(mapVehicleToForm(tractor));
        }
      } catch (error) {
        if (cancelled || seq !== loadSeqRef.current || !openRef.current) {
          return;
        }
        setAlertTitle("Erro");
        setAlertMsg(
          error?.response?.data?.message ||
            "Não foi possível carregar o veículo.",
        );
        setAlertOpen(true);
      } finally {
        if (seq === loadSeqRef.current) {
          setLoadingVehicle(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, vehicleId]);

  useEffect(() => {
    if (!open || !hasAttemptedSubmit) return;

    const result = isReboque
      ? validateReboqueForm(reboqueForm)
      : validateVehicleForm(form);
    setFieldErrors(result.success ? {} : result.fieldErrors);
  }, [open, hasAttemptedSubmit, isReboque, reboqueForm, form]);

  const handleIsReboqueChange = (checked) => {
    if (isEditMode) return;
    setIsReboque(checked);
    setFieldErrors({});
    setHasAttemptedSubmit(false);
    if (checked) {
      setReboqueForm(emptyReboque());
      if (!pendingStorageIdRef.current) {
        pendingStorageIdRef.current = createPendingStorageId();
      }
    }
  };

  const showDialog = (dialogTitle, dialogMsg) => {
    setAlertTitle(dialogTitle);
    setAlertMsg(dialogMsg);
    setAlertOpen(true);
  };

  const handleAlertClose = () => setAlertOpen(false);

  const handleRequestClose = () => {
    if (carregando || loadingVehicle) return;
    onClose?.();
  };

  const handleUpload = (file, onReady) => {
    if (!file) return;
    setUploading(true);
    const storageId =
      editingIdRef.current ||
      pendingStorageIdRef.current ||
      createPendingStorageId();
    pendingStorageIdRef.current = storageId;
    const storageRef = ref(storage, `vehicle/${storageId}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      () => {},
      (error) => {
        console.error(error);
        setUploading(false);
        if (!openRef.current) return;
        showDialog("Erro", "Falha no upload do documento.");
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setUploading(false);
        if (!openRef.current) return;
        onReady(downloadURL);
      },
    );
  };

  const handleUploadReboqueAntt = (file) => {
    handleUpload(file, (url) => {
      setReboqueForm((prev) => ({ ...prev, anttReboque: url }));
    });
  };

  const handleVehicleTypeChange = (value) => {
    const nextType = value || "";
    if (isVehicleTypeRequiringReboque(nextType)) {
      updateForm({
        vehicleType: nextType,
        pesoTara: "",
        capacidadeKG: "",
        capacidadeM3: "",
        bodyworkType: "",
        bodyworkPlate: "",
      });
      return;
    }
    updateForm({ vehicleType: nextType });
  };

  const handleCpfCnpjBlur = (valor) => {
    if (!valor || valor.trim() === "") return;
    if (valor === lastWarnedCpfCnpj) return;
    const clean = valor.replace(/\D/g, "");
    if (tipoProprietario === "cnpj") {
      if (!validatorCNPJ(clean)) {
        setLastWarnedCpfCnpj(valor);
        showDialog("Atenção", "CNPJ do proprietário inválido!");
      }
    } else if (!validatorCPF(clean)) {
      setLastWarnedCpfCnpj(valor);
      showDialog("Atenção", "CPF do proprietário inválido!");
    }
  };

  const getFieldError = (path) => fieldErrors[path] || "";

  const buildPayload = () => ({
    uidShipper: user?.uidShipper || "",
    trailers: false,
    vehiclePlate,
    renavam: renavam || "",
    ufVeiculo: ufVeiculo || "",
    documentCRLV: documentCRLV || "",
    descricaoVeiculo: descricaoVeiculo || "",
    vehicleType: vehicleType || "",
    rntrc: rntrc || "",
    ...(tipoRNTRC ? { tipoRNTRC } : {}),
    bodyworkType: bodyworkType || "",
    bodyworkPlate: bodyworkPlate || "",
    documentAnttFrontImg: documentAnttFrontImg || "",
    pesoTara: pesoTara || "",
    capacidadeKG: capacidadeKG || "",
    capacidadeM3: capacidadeM3 || "",
    ...(tipoProprietario ? { tipoProprietario } : {}),
    cpfoucnpj: cpfoucnpj || "",
    nomeProprietario: nomeProprietario || "",
    vehicleImg: vehicleImg || "",
    trailerIds: normalizeTrailerIds(trailerIds),
    reboques: mapEmbeddedReboquesToApiPayload(reboques, isReboqueStarted),
  });

  const handleSaveEmbeddedReboque = async (reboqueId, nextData) => {
    const tractorId = normalizePersistedVehicleId(editingIdRef.current);
    let nextReboques = [];
    let nextTrailerIds = [];

    setForm((prev) => {
      nextReboques = patchEmbeddedReboque(prev.reboques, reboqueId, nextData);
      nextTrailerIds = normalizeTrailerIds(prev.trailerIds);
      return { ...prev, reboques: nextReboques };
    });

    return saveEmbeddedReboqueOnTractor({
      tractorId,
      reboques: nextReboques,
      trailerIds: nextTrailerIds,
      isStarted: isReboqueStarted,
    });
  };

  const save = async () => {
    if (isBusy || savingRef.current) return;

    setHasAttemptedSubmit(true);

    if (isReboque) {
      const validation = validateReboqueForm(reboqueForm);
      if (!validation.success) {
        setFieldErrors(validation.fieldErrors);
        showDialog("Campos obrigatórios faltando", validation.messages);
        window.setTimeout(() => {
          focusFirstInvalidField(validation.firstFieldPath);
        }, 0);
        return;
      }

      const editingId = normalizePersistedVehicleId(editingIdRef.current);
      const shouldUpdate = Boolean(editingId);

      savingRef.current = true;
      setFieldErrors({});
      setCarregando(true);
      try {
        let response;
        let saved;

        if (shouldUpdate) {
          const payload = reboqueFormToTrailerUpdatePayload(
            reboqueForm,
            user?.uidShipper || "",
          );
          response = await vehicleService.update(editingId, payload);
          saved = resolveSavedVehicle(response, { ...payload, trailers: true }, editingId);
          if (!saved?.id || normalizePersistedVehicleId(saved.id) !== editingId) {
            saved = { ...payload, trailers: true, id: editingId };
          }
        } else {
          const payload = reboqueFormToTrailerCreatePayload(
            reboqueForm,
            user?.uidShipper || "",
          );
          response = await vehicleService.create(payload);
          saved = resolveSavedVehicle(response, payload);
          if (!saved?.id) {
            showDialog("Erro", "Não foi possível salvar o reboque.");
            return;
          }
        }

        const message =
          response?.data?.message ||
          (shouldUpdate
            ? "Reboque atualizado com sucesso"
            : "Reboque cadastrado com sucesso");

        const notify = onSaved || onCreated;
        notify?.(saved, message, {
          mode: shouldUpdate ? "edit" : "create",
          isReboque: true,
        });
        if (!notify) {
          onClose?.();
        }
      } catch (error) {
        const apiErrors = error?.response?.data?.errors;
        const message =
          error?.response?.data?.message ||
          (Array.isArray(apiErrors) && apiErrors[0]?.message) ||
          "Não foi possível salvar o reboque.";
        showDialog("Erro", message);
      } finally {
        savingRef.current = false;
        setCarregando(false);
      }
      return;
    }

    const validation = validateVehicleForm(form);
    if (!validation.success) {
      setFieldErrors(validation.fieldErrors);
      showDialog("Campos obrigatórios faltando", validation.messages);
      window.setTimeout(() => {
        focusFirstInvalidField(validation.firstFieldPath);
      }, 0);
      return;
    }

    const editingId = normalizePersistedVehicleId(editingIdRef.current);
    const shouldUpdate = Boolean(editingId);

    savingRef.current = true;
    setFieldErrors({});
    setCarregando(true);
    try {
      const payload = buildPayload();
      let response;
      let saved;

      if (shouldUpdate) {
        response = await vehicleService.update(editingId, payload);
        saved = resolveSavedVehicle(response, payload, editingId);
        if (!saved?.id || normalizePersistedVehicleId(saved.id) !== editingId) {
          saved = { ...payload, id: editingId };
        }
      } else {
        response = await vehicleService.create(payload);
        saved = resolveSavedVehicle(response, payload);
        if (!saved?.id) {
          showDialog("Erro", "Não foi possível salvar o veículo.");
          return;
        }
      }

      const message =
        response?.data?.message ||
        (shouldUpdate
          ? "Veículo atualizado com sucesso"
          : "Veículo cadastrado com sucesso");

      const notify = onSaved || onCreated;
      notify?.(saved, message, {
        mode: shouldUpdate ? "edit" : "create",
        isReboque: false,
      });
      if (!notify) {
        onClose?.();
      }
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      const message =
        error?.response?.data?.message ||
        (Array.isArray(apiErrors) && apiErrors[0]?.message) ||
        "Não foi possível salvar o veículo.";
      showDialog("Erro", message);
    } finally {
      savingRef.current = false;
      setCarregando(false);
    }
  };

  const dialogMessageItems = parseDialogMessageItems(alertMsg);
  const requiredFieldsDialog = isRequiredFieldsDialog(alertTitle);
  const showErrorList =
    requiredFieldsDialog ||
    (Array.isArray(alertMsg) && dialogMessageItems.length > 0);
  const dialogDescription = requiredFieldsDialog
    ? "Os seguintes campos obrigatórios estão vazios:"
    : null;
  const dialogBodyText = !showErrorList ? String(alertMsg || "") : "";
  const DialogAlertIcon = requiredFieldsDialog
    ? WarningAmberRoundedIcon
    : ErrorOutlineRoundedIcon;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleRequestClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        aria-labelledby="quick-vehicle-modal-title"
        PaperProps={{ sx: modalPaperSx }}
      >
        <Box
          className="new-vehicle-page"
          style={newClientThemeVars}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <Box sx={modalHeaderSx}>
            <Typography
              id="quick-vehicle-modal-title"
              component="h2"
              sx={modalTitleSx}
            >
              {isEditMode
                ? isReboque
                  ? "Editar Reboque"
                  : "Editar Veículo"
                : isReboque
                  ? "Cadastro de Reboque"
                  : "Cadastro de Veículo"}
            </Typography>
          </Box>

          <DialogContent sx={modalContentSx} dividers>
            {loadingVehicle ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 220,
                  py: 4,
                }}
              >
                <CircularProgress size={36} />
              </Box>
            ) : (
              <>
            <FormControlLabel
              control={
                <Switch
                  checked={isReboque}
                  disabled={isEditMode}
                  onChange={(e) => handleIsReboqueChange(e.target.checked)}
                  color="primary"
                />
              }
              label="É reboque"
              sx={{ mb: 1.5, ml: 0, color: "rgba(1,36,67,0.82)" }}
            />

            {isReboque ? (
              <>
                <Typography sx={sectionTitleSx}>Dados do reboque</Typography>
                <ReboqueFormFields
                  value={reboqueForm}
                  onChange={setReboqueForm}
                  fieldErrors={fieldErrors}
                  uploading={uploading}
                  onUploadAntt={handleUploadReboqueAntt}
                />
              </>
            ) : (
              <>
            <Typography sx={sectionTitleSx}>Dados do veículo</Typography>
            <Box className="new-vehicle-grid">
              <Field
                label="Placa do veículo *"
                span={3}
                fieldPath="vehiclePlate"
                error={getFieldError("vehiclePlate")}
              >
                <InputMask
                  mask="aaa-9*99"
                  maskChar=""
                  className="form-control"
                  value={vehiclePlate}
                  onChange={(e) =>
                    updateForm({ vehiclePlate: e.target.value.toUpperCase() })
                  }
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      aria-invalid={Boolean(getFieldError("vehiclePlate"))}
                      aria-describedby={
                        getFieldError("vehiclePlate")
                          ? "vehicle-field-vehiclePlate-error"
                          : undefined
                      }
                    />
                  )}
                </InputMask>
              </Field>

              <Field
                label="Renavam *"
                span={3}
                fieldPath="renavam"
                error={getFieldError("renavam")}
              >
                <Input
                  maxLength={11}
                  status={getFieldError("renavam") ? "error" : undefined}
                  value={renavam}
                  onChange={(e) =>
                    updateForm({
                      renavam: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  aria-invalid={Boolean(getFieldError("renavam"))}
                  aria-describedby={
                    getFieldError("renavam")
                      ? "vehicle-field-renavam-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label="UF Veículo *"
                span={2}
                fieldPath="ufVeiculo"
                error={getFieldError("ufVeiculo")}
              >
                <Select
                  style={{ width: "100%" }}
                  status={getFieldError("ufVeiculo") ? "error" : undefined}
                  value={ufVeiculo || undefined}
                  placeholder="Selecione"
                  onChange={(value) => updateForm({ ufVeiculo: value || "" })}
                  options={STATES_UF.map((s) => ({
                    value: s.value,
                    label: `${s.label} (${s.value})`,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                  aria-invalid={Boolean(getFieldError("ufVeiculo"))}
                  aria-describedby={
                    getFieldError("ufVeiculo")
                      ? "vehicle-field-ufVeiculo-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label="Tipo de veículo *"
                span={4}
                fieldPath="vehicleType"
                error={getFieldError("vehicleType")}
              >
                <Select
                  style={{ width: "100%" }}
                  status={getFieldError("vehicleType") ? "error" : undefined}
                  value={vehicleType || undefined}
                  placeholder="Selecione"
                  onChange={handleVehicleTypeChange}
                  options={VEHICLE_TYPES.map((t) => ({
                    value: t,
                    label: t,
                  }))}
                  allowClear
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                  aria-invalid={Boolean(getFieldError("vehicleType"))}
                  aria-describedby={
                    getFieldError("vehicleType")
                      ? "vehicle-field-vehicleType-error"
                      : undefined
                  }
                />
              </Field>

              <Field label="Descrição Veículo" span={6}>
                <Input
                  maxLength={200}
                  value={descricaoVeiculo}
                  onChange={(e) =>
                    updateForm({ descricaoVeiculo: e.target.value })
                  }
                />
              </Field>

              <Field
                label="RNTRC *"
                span={2}
                fieldPath="rntrc"
                error={getFieldError("rntrc")}
              >
                <Input
                  maxLength={8}
                  status={getFieldError("rntrc") ? "error" : undefined}
                  value={rntrc}
                  onChange={(e) =>
                    updateForm({ rntrc: e.target.value.replace(/\D/g, "") })
                  }
                  aria-invalid={Boolean(getFieldError("rntrc"))}
                  aria-describedby={
                    getFieldError("rntrc")
                      ? "vehicle-field-rntrc-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label="Tipo RNTRC *"
                span={2}
                fieldPath="tipoRNTRC"
                error={getFieldError("tipoRNTRC")}
              >
                <Select
                  style={{ width: "100%" }}
                  status={getFieldError("tipoRNTRC") ? "error" : undefined}
                  value={tipoRNTRC || undefined}
                  placeholder="Selecione"
                  onChange={(value) => updateForm({ tipoRNTRC: value || "" })}
                  options={RNTRC_TYPE_OPTIONS}
                  allowClear
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                  aria-invalid={Boolean(getFieldError("tipoRNTRC"))}
                  aria-describedby={
                    getFieldError("tipoRNTRC")
                      ? "vehicle-field-tipoRNTRC-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label={
                  requiresReboque ? "Tipo Carroceria" : "Tipo Carroceria *"
                }
                span={3}
                fieldPath="bodyworkType"
                error={getFieldError("bodyworkType")}
              >
                <Select
                  disabled={capacityFieldsDisabled}
                  style={{ width: "100%" }}
                  status={getFieldError("bodyworkType") ? "error" : undefined}
                  value={bodyworkType || undefined}
                  placeholder="Selecione"
                  onChange={(value) =>
                    updateForm({ bodyworkType: value || "" })
                  }
                  options={BODYWORK_TYPES.map((t) => ({
                    value: t,
                    label: t,
                  }))}
                  allowClear
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                  aria-invalid={Boolean(getFieldError("bodyworkType"))}
                  aria-describedby={
                    getFieldError("bodyworkType")
                      ? "vehicle-field-bodyworkType-error"
                      : undefined
                  }
                />
              </Field>

              <Field label="Placa carroceria" span={3}>
                <InputMask
                  disabled={capacityFieldsDisabled}
                  mask="aaa-9*99"
                  maskChar=""
                  className="form-control"
                  value={bodyworkPlate}
                  onChange={(e) =>
                    updateForm({
                      bodyworkPlate: e.target.value.toUpperCase(),
                    })
                  }
                >
                  {(inputProps) => <input {...inputProps} />}
                </InputMask>
              </Field>

              <Field label="Peso (Tara)" span={2}>
                <Input
                  disabled={capacityFieldsDisabled}
                  value={pesoTara}
                  onChange={(e) =>
                    updateForm({
                      pesoTara: formatWeightMask(e.target.value),
                    })
                  }
                />
              </Field>

              <Field label="Capacidade (KG)" span={2}>
                <Input
                  disabled={capacityFieldsDisabled}
                  value={capacidadeKG}
                  onChange={(e) =>
                    updateForm({
                      capacidadeKG: formatWeightMask(e.target.value),
                    })
                  }
                />
              </Field>

              <Field label="Capacidade (M³)" span={2}>
                <Input
                  disabled={capacityFieldsDisabled}
                  value={capacidadeM3}
                  onChange={(e) =>
                    updateForm({
                      capacidadeM3: formatWeightMask(e.target.value),
                    })
                  }
                />
              </Field>

              <Field label="Tipo Proprietário" span={2}>
                <Select
                  style={{ width: "100%" }}
                  value={tipoProprietario || undefined}
                  onChange={(value) =>
                    updateForm({
                      tipoProprietario: value || "",
                      cpfoucnpj: "",
                    })
                  }
                  options={[
                    { value: "cpf", label: "CPF" },
                    { value: "cnpj", label: "CNPJ" },
                  ]}
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                />
              </Field>

              <Field
                label={
                  tipoProprietario === "cnpj"
                    ? "CNPJ Proprietário"
                    : "CPF Proprietário"
                }
                span={4}
              >
                <InputMask
                  mask={
                    tipoProprietario === "cnpj"
                      ? "99.999.999/9999-99"
                      : "999.999.999-99"
                  }
                  maskChar=""
                  className="form-control"
                  value={cpfoucnpj}
                  onChange={(e) => {
                    updateForm({ cpfoucnpj: e.target.value });
                    setLastWarnedCpfCnpj("");
                  }}
                  onBlur={(e) => handleCpfCnpjBlur(e.target.value)}
                >
                  {(inputProps) => <input {...inputProps} />}
                </InputMask>
              </Field>

              <Field label="Nome Proprietário" span={6}>
                <Input
                  value={nomeProprietario}
                  onChange={(e) =>
                    updateForm({ nomeProprietario: e.target.value })
                  }
                />
              </Field>
            </Box>

            <Typography sx={{ ...sectionTitleSx, mt: 2.5 }}>
              Documentos
            </Typography>
            <Box className="new-vehicle-grid">
              <Field
                label="CRLV *"
                span={6}
                fieldPath="documentCRLV"
                error={getFieldError("documentCRLV")}
              >
                <DocumentField
                  url={documentCRLV}
                  disabled={uploading}
                  error={Boolean(getFieldError("documentCRLV"))}
                  errorId="vehicle-field-documentCRLV-error"
                  onUpload={(file) =>
                    handleUpload(file, (url) =>
                      updateForm({ documentCRLV: url }),
                    )
                  }
                  onClear={() => updateForm({ documentCRLV: "" })}
                />
              </Field>
              <Field
                label="Doc ANTT *"
                span={6}
                fieldPath="documentAnttFrontImg"
                error={getFieldError("documentAnttFrontImg")}
              >
                <DocumentField
                  url={documentAnttFrontImg}
                  disabled={uploading}
                  error={Boolean(getFieldError("documentAnttFrontImg"))}
                  errorId="vehicle-field-documentAnttFrontImg-error"
                  onUpload={(file) =>
                    handleUpload(file, (url) =>
                      updateForm({ documentAnttFrontImg: url }),
                    )
                  }
                  onClear={() => updateForm({ documentAnttFrontImg: "" })}
                />
              </Field>
              <Field label="Imagem do veículo" span={6}>
                <DocumentField
                  url={vehicleImg}
                  disabled={uploading}
                  onUpload={(file) =>
                    handleUpload(file, (url) =>
                      updateForm({ vehicleImg: url }),
                    )
                  }
                  onClear={() => updateForm({ vehicleImg: "" })}
                />
              </Field>
            </Box>

            <VehicleTrailersSection
              trailerIds={trailerIds}
              embeddedReboques={reboques}
              onTrailerIdsChange={(ids) =>
                updateForm({ trailerIds: normalizeTrailerIds(ids) })
              }
              onEmbeddedReboquesChange={(next) =>
                updateForm({ reboques: next })
              }
              requiresReboque={requiresReboque}
              selectionError={
                getFieldError("trailerIds") || getFieldError("reboques")
              }
              tractorVehicleId={persistedVehicleId}
              onSaveEmbedded={handleSaveEmbeddedReboque}
            />
              </>
            )}
              </>
            )}
          </DialogContent>

          <DialogActions sx={modalActionsSx}>
            <Button
              onClick={handleRequestClose}
              variant="outlined"
              disabled={carregando || loadingVehicle}
              sx={secondaryActionSx}
            >
              Cancelar
            </Button>
            <Button
              disabled={isBusy}
              variant="contained"
              startIcon={
                carregando ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null
              }
              onClick={save}
              sx={primaryActionSx}
            >
              {carregando
                ? isEditMode
                  ? "Salvando..."
                  : "Cadastrando..."
                : isEditMode
                  ? "Salvar"
                  : isReboque
                    ? "Cadastrar reboque"
                    : "Cadastrar"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={alertOpen}
        onClose={handleAlertClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ALERT_TITLE_ID}
        aria-describedby={ALERT_DESCRIPTION_ID}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        BackdropProps={{
          sx: dialogBackdropSx,
        }}
        PaperProps={{
          sx: alertPaperSx,
        }}
      >
        <Box sx={dialogHeaderSx}>
          <Box sx={dialogIconWrapSx}>
            <DialogAlertIcon
              sx={{
                fontSize: 26,
                color: theme.colors.highlight,
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography id={ALERT_TITLE_ID} component="h2" sx={dialogTitleSx}>
              {alertTitle || "Atenção"}
            </Typography>
            {dialogDescription ? (
              <Typography id={ALERT_DESCRIPTION_ID} sx={dialogSubtitleSx}>
                {dialogDescription}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <DialogContent
          id={dialogDescription ? undefined : ALERT_DESCRIPTION_ID}
          sx={dialogContentSx}
        >
          {showErrorList ? (
            <Box
              component="ul"
              sx={dialogErrorListSx}
              aria-label="Lista de campos obrigatórios"
            >
              {dialogMessageItems.map((item, index) => (
                <Box
                  component="li"
                  key={`${item}-${index}`}
                  sx={dialogErrorItemSx}
                >
                  <Box component="span" sx={dialogErrorBulletSx} />
                  <Typography component="span" sx={dialogErrorTextSx}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={dialogBodyTextSx}>{dialogBodyText}</Typography>
          )}
        </DialogContent>

        <Divider sx={{ borderColor: theme.colors.primaryBorder }} />

        <DialogActions sx={dialogActionsSx}>
          <Button
            onClick={handleAlertClose}
            variant="contained"
            autoFocus
            disableElevation
            sx={dialogPrimaryButtonSx}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Field({ label, span = 3, error, fieldPath, children }) {
  const errorId = fieldPath
    ? `vehicle-field-${String(fieldPath).replace(/\./g, "-")}-error`
    : undefined;

  return (
    <div
      className={`new-vehicle-field new-vehicle-span-${span}${error ? " has-error" : ""}`}
      data-field-path={fieldPath || undefined}
    >
      <label>{label}</label>
      {children}
      {error ? (
        <span id={errorId} className="new-vehicle-field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

const modalPaperSx = {
  borderRadius: `${theme.radius.large}px`,
  overflow: "hidden",
};

const modalHeaderSx = {
  px: { xs: 2, sm: 2.5 },
  pt: { xs: 2, sm: 2.5 },
  pb: 1.5,
  borderBottom: `1px solid ${theme.colors.primaryBorder}`,
};

const modalTitleSx = {
  color: theme.colors.primary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightBold,
  fontSize: { xs: "1.05rem", sm: "1.2rem" },
  lineHeight: 1.3,
};

const modalContentSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 2, sm: 2.25 },
};

const modalActionsSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.5, sm: 2 },
  justifyContent: "flex-end",
  gap: 1,
  borderTop: `1px solid ${theme.colors.primaryBorder}`,
};

const sectionTitleSx = {
  color: "var(--new-client-primary)",
  fontWeight: 700,
  fontSize: "1.05rem",
  mb: 1.5,
};

const sectionSubtitleSx = {
  color: "rgba(1, 36, 67, 0.62)",
  fontSize: "0.79rem",
  lineHeight: 1.45,
};

const primaryActionSx = {
  textTransform: "none",
  fontWeight: theme.typography.weightSemibold,
  borderRadius: `${theme.radius.medium - 2}px`,
  backgroundColor: "var(--new-client-primary)",
  minHeight: 40,
  px: 2,
  "&:hover": {
    backgroundColor: "var(--new-client-primary-hover)",
  },
};

const secondaryActionSx = {
  textTransform: "none",
  fontWeight: theme.typography.weightSemibold,
  borderRadius: `${theme.radius.medium - 2}px`,
  minHeight: 40,
  color: "var(--new-client-primary)",
  borderColor: "var(--new-client-primary-border)",
};

const dialogBackdropSx = {
  backgroundColor: "rgba(1, 36, 66, 0.48)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
};

const alertPaperSx = {
  borderRadius: `${theme.radius.large}px`,
  backgroundColor: theme.colors.surface,
  boxShadow: "0 18px 40px rgba(1, 36, 66, 0.18)",
  overflow: "hidden",
  width: "100%",
  maxWidth: 520,
  mx: { xs: 2, sm: 3 },
  my: { xs: 2, sm: 3 },
  maxHeight: "min(88vh, 640px)",
  display: "flex",
  flexDirection: "column",
  transition: theme.motion.transition,
};

const dialogHeaderSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: `${theme.spacing.sm}px`,
  px: { xs: 2, sm: 2.5 },
  pt: { xs: 2, sm: 2.5 },
  pb: { xs: 1.5, sm: 2 },
  backgroundColor: theme.colors.highlightSoft,
  borderBottom: `1px solid ${theme.colors.primaryBorder}`,
};

const dialogIconWrapSx = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  backgroundColor: theme.colors.surface,
  border: `1px solid ${theme.colors.primaryBorder}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 4px 12px rgba(1, 36, 66, 0.08)",
};

const dialogTitleSx = {
  color: theme.colors.primary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightBold,
  fontSize: { xs: "1.05rem", sm: "1.2rem" },
  lineHeight: 1.3,
  letterSpacing: theme.typography.letterSpacing,
};

const dialogSubtitleSx = {
  mt: 0.5,
  color: theme.colors.textSecondary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightRegular,
  fontSize: theme.typography.bodySize,
  lineHeight: theme.typography.bodyLineHeight,
};

const dialogContentSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.75, sm: 2 },
  overflowY: "auto",
  maxHeight: { xs: "42vh", sm: "46vh" },
};

const dialogErrorListSx = {
  listStyle: "none",
  m: 0,
  p: 0,
  display: "flex",
  flexDirection: "column",
  gap: `${theme.spacing.xs}px`,
};

const dialogErrorItemSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: `${theme.spacing.xs}px`,
  px: 1.25,
  py: 1,
  borderRadius: `${theme.radius.small}px`,
  backgroundColor: theme.colors.highlightSoft,
  border: `1px solid ${theme.colors.primaryBorder}`,
};

const dialogErrorBulletSx = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  backgroundColor: theme.colors.highlight,
  flexShrink: 0,
  mt: "0.45rem",
};

const dialogErrorTextSx = {
  color: theme.colors.textPrimary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.bodySize,
  lineHeight: 1.45,
  fontWeight: theme.typography.weightSemibold,
  wordBreak: "break-word",
};

const dialogBodyTextSx = {
  color: theme.colors.textSecondary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.bodySize,
  lineHeight: 1.5,
  whiteSpace: "pre-line",
  wordBreak: "break-word",
};

const dialogActionsSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.5, sm: 2 },
  justifyContent: { xs: "stretch", sm: "flex-end" },
  gap: 1,
};

const dialogPrimaryButtonSx = {
  textTransform: "none",
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightSemibold,
  borderRadius: `${theme.radius.medium - 2}px`,
  backgroundColor: theme.colors.primary,
  color: theme.colors.white,
  border: `1px solid ${theme.colors.primary}`,
  minHeight: 42,
  minWidth: { xs: "100%", sm: 112 },
  px: 2.5,
  transition: theme.motion.transition,
  "&:hover": {
    backgroundColor: theme.colors.white,
    color: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
};

export default QuickVehicleModal;
